/**
 * SyncStatusBanner.jsx
 *
 * Displays a dismissible alert when there are permanently failed sync
 * operations in the offline queue. Items are grouped by type so the
 * banner stays manageable even when many students / saves are involved.
 *
 * Usage: render once near the top of the main Layout so it is globally visible.
 */

import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { getFailedItems, dismissFailedItem, getSyncStatus, runSync } from "../../app/syncService";
import db from "../../app/offlineDb";
import { getOfflineContext } from "../../app/offlineContext";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const GROUP_LABELS = {
  record: "Score entries",
  comment: "Comments",
  trait_grade: "Behaviour / trait grades",
  quiz_answer: "Quiz answers",
};

/** Format a list of names into "Alice, Bob and 3 more" */
function formatNames(names, max = 2) {
  if (names.length === 0) return null;
  if (names.length <= max) return names.join(", ");
  const shown = names.slice(0, max).join(", ");
  return `${shown} and ${names.length - max} more`;
}

async function getStudentMap(classroomId) {
  if (!classroomId) return {};
  const ctx = getOfflineContext();
  if (!ctx.schoolId || !ctx.userId || !ctx.termId) return {};
  const row = await db.students.get([ctx.schoolId, ctx.userId, ctx.termId, classroomId]).catch(() => null);
  if (!row?.data) return {};
  return Object.fromEntries(row.data.map((s) => [String(s.id), `${s.first_name} ${s.last_name}`]));
}

async function getAssessmentMap(classroomId) {
  if (!classroomId) return {};
  const ctx = getOfflineContext();
  if (!ctx.schoolId || !ctx.userId || !ctx.termId) return {};
  const row = await db.assessments.get([ctx.schoolId, ctx.userId, ctx.termId, classroomId]).catch(() => null);
  if (!row?.data) return {};
  return Object.fromEntries(row.data.map((a) => [String(a.id), a.name ?? `Assessment ${a.id}`]));
}

/**
 * Returns { title, rows } where rows is an array of { studentName, scores[] }
 * for compact per-student display. Row count is capped at MAX_STUDENT_ROWS.
 */
const MAX_STUDENT_ROWS = 6;

async function describeItem(item) {
  const { entityType, classroomId, subjectId, studentId, payload } = item;
  const conflictPrefix = item?.syncOutcome === "server_newer"
    ? "Not synced because newer server data already exists. "
    : "";

  if (entityType === "record") {
    const records = payload?.records ?? [];
    if (records.length === 0) return { title: "Saved scores", rows: null, extra: 0 };

    const [studentMap, assessmentMap] = await Promise.all([
      getStudentMap(classroomId),
      getAssessmentMap(classroomId),
    ]);

    // Group by student
    const byStudent = {};
    for (const r of records) {
      const sid = String(r.student_id);
      byStudent[sid] = byStudent[sid] ?? [];
      byStudent[sid].push(r);
    }

    const studentIds = Object.keys(byStudent);
    const allNames = studentIds.map((id) => studentMap[id]).filter(Boolean);
    const title = allNames.length > 0
      ? `Scores for ${formatNames(allNames)}`
      : `Scores — ${studentIds.length} student${studentIds.length !== 1 ? "s" : ""}`;

    const visibleIds = studentIds.slice(0, MAX_STUDENT_ROWS);
    const extra = studentIds.length - visibleIds.length;

    const rows = visibleIds.map((sid) => ({
      studentName: studentMap[sid] ?? `Student ${sid}`,
      scores: byStudent[sid].map((r) => ({
        assessment: assessmentMap[String(r.assessment_id)] ?? `Asmt ${r.assessment_id}`,
        score: r.score,
      })),
    }));

    return { title, rows, extra, note: item?.syncOutcome === "server_newer" ? conflictPrefix.trim() : null };
  }

  if (entityType === "comment") {
    const studentMap = await getStudentMap(classroomId);
    const name = studentId ? studentMap[String(studentId)] : null;
    const text = payload?.comment ?? payload?.text ?? payload?.comments?.[0]?.content ?? "";
    const preview = text.length > 100 ? text.slice(0, 100) + "…" : text;
    return {
      title: name ? `Comment for ${name}` : "Teacher comment",
      rows: preview ? [{ studentName: null, scores: null, text: preview }] : null,
      extra: 0,
      note: item?.syncOutcome === "server_newer" ? conflictPrefix.trim() : null,
    };
  }

  if (entityType === "trait_grade") {
    const studentMap = await getStudentMap(classroomId);
    const name = studentId ? studentMap[String(studentId)] : null;
    const grades = payload?.grades ?? [];
    return {
      title: name ? `Behaviour grades for ${name}` : "Behaviour / trait grades",
      rows: grades.length > 0 ? [{ studentName: null, scores: null, text: `${grades.length} trait${grades.length !== 1 ? "s" : ""} graded` }] : null,
      extra: 0,
      note: item?.syncOutcome === "server_newer" ? conflictPrefix.trim() : null,
    };
  }

  if (entityType === "quiz_answer") {
    const studentMap = await getStudentMap(classroomId);
    const name = studentId ? studentMap[String(studentId)] : null;
    return {
      title: name ? `Quiz answer from ${name}` : "Quiz answer",
      rows: null,
      extra: 0,
    };
  }

  return { title: "Unsaved change", rows: null, extra: 0 };
}

// ---------------------------------------------------------------------------
// Single failed item card (used inside a group)
// ---------------------------------------------------------------------------
function FailedItem({ item, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [desc, setDesc] = useState(null);

  useEffect(() => {
    describeItem(item).then(setDesc);
  }, [item]);

  const title = desc?.title ?? "Loading…";
  const rows = desc?.rows ?? null;
  const extra = desc?.extra ?? 0;
  const note = desc?.note ?? null;
  const hasDetail = rows && rows.length > 0;

  // Check if rows are score-table format or plain text
  const isScoreTable = hasDetail && rows[0]?.scores !== null && rows[0]?.scores !== undefined;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "error.light",
        borderRadius: 1,
        p: 1,
        mb: 0.5,
        backgroundColor: "error.50",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
          {title}
        </Typography>

        {hasDetail && (
          <Tooltip title={expanded ? "Hide details" : "Show details"}>
            <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Dismiss — once you've re-entered this data manually">
          <IconButton size="small" onClick={() => onDismiss(item.id)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {hasDetail && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 0.5, overflowX: "auto" }}>
            {isScoreTable ? (
              <>
                <Table size="small" sx={{ fontSize: "0.72rem" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 0.25, px: 1 }}>Student</TableCell>
                      {rows[0]?.scores?.map((s) => (
                        <TableCell key={s.assessment} sx={{ fontWeight: 600, py: 0.25, px: 1 }}>
                          {s.assessment}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ py: 0.25, px: 1 }}>{row.studentName}</TableCell>
                        {row.scores.map((s, j) => (
                          <TableCell key={j} sx={{ py: 0.25, px: 1 }}>{s.score}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {extra > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                    + {extra} more student{extra !== 1 ? "s" : ""} (see full data above)
                  </Typography>
                )}
              </>
            ) : (
              rows.map((row, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: "block", pl: 1 }}>
                  {row.text}
                </Typography>
              ))
            )}
          </Box>
        </Collapse>
      )}

      {note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Group of failed items of the same entity type
// ---------------------------------------------------------------------------
function FailedGroup({ entityType, items, onDismiss, onDismissAll }) {
  const [expanded, setExpanded] = useState(true);
  const label = GROUP_LABELS[entityType] ?? "Unsaved changes";

  return (
    <Box sx={{ mb: 1 }}>
      {/* Group header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          py: 0.5,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          {label}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({items.length})
          </Typography>
        </Typography>
        <Tooltip title={`Dismiss all ${label.toLowerCase()} — once re-entered`}>
          <Button
            size="small"
            color="error"
            onClick={(e) => { e.stopPropagation(); onDismissAll(items.map((i) => i.id)); }}
            sx={{ minWidth: 0, px: 1, fontSize: "0.7rem" }}
          >
            Dismiss all
          </Button>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        {items.map((item) => (
          <FailedItem key={item.id} item={item} onDismiss={onDismiss} />
        ))}
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main banner
// ---------------------------------------------------------------------------
export default function SyncStatusBanner() {
  const [failedItems, setFailedItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const refresh = useCallback(async () => {
    const [items, status] = await Promise.all([getFailedItems(), getSyncStatus()]);
    setFailedItems(items);
    setPending(status.pending);
    if (items.length > 0) setBannerDismissed(false);
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10_000);
    return () => clearInterval(interval);
  }, [refresh]);

  const handleDismiss = async (id) => {
    await dismissFailedItem(id);
    await refresh();
  };

  const handleDismissAll = async (ids) => {
    await Promise.all(ids.map(dismissFailedItem));
    await refresh();
  };

  const handleRetryAll = async () => {
    const failed = await db.syncQueue.where("status").equals("failed").toArray();
    for (const item of failed) {
      await db.syncQueue.update(item.id, { status: "pending", retries: 0 });
    }
    await runSync();
    await refresh();
  };

  if (bannerDismissed || (failedItems.length === 0 && pending === 0)) return null;

  // Group failed items by entity type
  const groups = failedItems.reduce((acc, item) => {
    const key = item.entityType ?? "unknown";
    acc[key] = acc[key] ?? [];
    acc[key].push(item);
    return acc;
  }, {});
  const groupEntries = Object.entries(groups);

  return (
    <Alert
      severity={failedItems.length > 0 ? "error" : "info"}
      sx={{ mb: 2, mx: 2 }}
      action={
        <IconButton size="small" onClick={() => setBannerDismissed(true)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <AlertTitle>
        {failedItems.length > 0
          ? `${failedItems.length} change${failedItems.length > 1 ? "s" : ""} couldn't be saved`
          : `Saving ${pending} change${pending > 1 ? "s" : ""}…`}
      </AlertTitle>

      {failedItems.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These were entered while offline but couldn't be uploaded. Please
            re-enter them, then tap ✕ (or "Dismiss all") to clear each group.
          </Typography>

          <Box sx={{ maxHeight: 380, overflowY: "auto", mb: 1 }}>
            {groupEntries.map(([type, items]) => (
              <FailedGroup
                key={type}
                entityType={type}
                items={items}
                onDismiss={handleDismiss}
                onDismissAll={handleDismissAll}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" color="error" onClick={handleRetryAll}>
              Try again
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={() => handleDismissAll(failedItems.map((i) => i.id))}
            >
              Dismiss all
            </Button>
          </Stack>
        </>
      )}

      {pending > 0 && failedItems.length === 0 && (
        <Typography variant="body2">
          Your changes will be uploaded automatically once the connection is restored.
        </Typography>
      )}
    </Alert>
  );
}
